import React from "react";
import { motion } from "motion/react";
import { GroupSessionResponse } from "../../models/GroupSessionResponse";

interface InviteSessionCardProps {
  s: GroupSessionResponse;
  onAccept: () => void;
  onDecline: () => void;
  onOpen?: () => void; // optional: click on body should open join as well
}

const EnvelopeIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden fill="currentColor">
    <path d="M20 4H4a2 2 0 0 0-2 2v1.2l10 5.6 10-5.6V6a2 2 0 0 0-2-2Zm0 5.2-8.8 4.92a1 1 0 0 1-.96 0L1.5 9.2V18a2 2 0 0 0 2 2h17a2 2 0 0 0 2-2V9.2Z" />
  </svg>
);

/**
 * Invite-only card to display annotated sessions (status === "INVITED").
 * Same footprint as SessionCard and visually aligned with your design.
 */
const InviteSessionCard: React.FC<InviteSessionCardProps> = ({ s, onAccept, onDecline, onOpen }) => {
  return (
    <motion.li
      layout
      className="snap-start shrink-0 w-[250px] h-[290px] mr-6 last:mr-0"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
    >
      <div
        className="
          relative h-full rounded-[28px] p-6
          bg-white/40 backdrop-blur
          border border-white/50
          transition-shadow
          flex flex-col
          hover:shadow-md
        "
      >
        {/* Top pill: Invitation */}
        <div className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm text-[#232323]">
          <span className="grid place-items-center w-7 h-7 rounded-full bg-violet-500/90 text-white">
            <EnvelopeIcon className="w-3.5 h-3.5" />
          </span>
          <span className="text-[15px] font-semibold">Invitation</span>
        </div>

        {/* Title + optional subtitle */}
        <button
          type="button"
          onClick={onOpen ?? onAccept}
          className="mt-6 mb-8 text-left"
          title="Open invitation"
        >
          <h3 className="text-[26px] leading-[1.1] font-extrabold tracking-tight text-[#232323] line-clamp-2">
            {s?.name || "Untitled session"}
          </h3>
        </button>

        {/* Actions */}
        <div className="mt-auto grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onDecline}
            className="
              rounded-full px-4 py-2 text-sm font-semibold
              bg-white text-rose-600 border border-rose-200
              hover:bg-rose-50 transition
            "
            title="Decline invitation"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={onAccept}
            className="
              rounded-full px-4 py-2 text-sm font-semibold
              text-white bg-[#A87AF0]
              hover:brightness-110 active:scale-[0.99]
              transition
            "
            title="Accept invitation"
          >
            Accept
          </button>
        </div>
      </div>
    </motion.li>
  );
};

export default InviteSessionCard;
